import { useState, useRef } from "react";
import { api, formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, X, ImageIcon, Loader2 } from "lucide-react";

// Resolve a stored image reference (URL or absolute) to a real <img src>.
export function resolveImageUrl(u) {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("/api/files/")) return `${API}${u.replace(/^\/api/, "")}`;
  return u;
}

export default function ImageUploader({ images, onChange, maxImages = 10 }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const openPicker = () => inputRef.current?.click();

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const slots = maxImages - images.length;
    if (slots <= 0) { toast.error(`Max ${maxImages} photos`); return; }
    const list = Array.from(files).slice(0, slots);
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of list) {
        if (!file.type.startsWith("image/")) {
          toast.error(`${file.name}: not an image`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", file);
        try {
          const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          uploaded.push({ id: data.file_id, url: data.url });
        } catch (e) {
          toast.error(`${file.name}: ${formatApiError(e.response?.data?.detail)}`);
        }
      }
      if (uploaded.length) {
        onChange([...images, ...uploaded]);
        toast.success(`${uploaded.length} photo${uploaded.length > 1 ? "s" : ""} uploaded`);
      }
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (id) => onChange(images.filter((i) => i.id !== id));

  const onDrop = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div data-testid="image-uploader">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => handleFiles(e.target.files)}
        data-testid="dock-photo-file-input"
        className="hidden"
      />

      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-3">
          {images.map((img, i) => (
            <div key={img.id} data-testid={`uploaded-photo-${i}`} className="group relative aspect-square rounded-lg overflow-hidden border border-border bg-secondary">
              <img src={resolveImageUrl(img.url)} alt="" className="h-full w-full object-cover"/>
              <button
                type="button"
                data-testid={`remove-photo-${i}`}
                onClick={() => remove(img.id)}
                className="absolute top-1.5 right-1.5 h-7 w-7 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                title="Remove"
              >
                <X className="h-3.5 w-3.5"/>
              </button>
              {i === 0 && (
                <div className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[10px] uppercase tracking-widest text-center py-1">
                  Cover
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        onClick={openPicker}
        role="button"
        tabIndex={0}
        data-testid="upload-dropzone"
        className={`rounded-xl border-2 border-dashed cursor-pointer transition-colors p-8 text-center ${uploading ? "border-accent bg-accent/5" : "border-border hover:border-primary/60 hover:bg-secondary/40"}`}
      >
        {uploading ? (
          <div className="flex flex-col items-center gap-2 text-accent">
            <Loader2 className="h-6 w-6 animate-spin"/>
            <span className="text-sm">Uploading…</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            {images.length === 0 ? <ImageIcon className="h-8 w-8" strokeWidth={1.5}/> : <Upload className="h-6 w-6" strokeWidth={1.5}/>}
            <div className="text-sm">
              <span className="font-medium text-foreground">Tap to choose photos</span> or drag & drop
            </div>
            <div className="text-xs">JPG, PNG, WebP, or HEIC · up to 12 MB each · {maxImages - images.length} slot{maxImages - images.length === 1 ? "" : "s"} left</div>
          </div>
        )}
      </div>
      <Button type="button" variant="outline" size="sm" onClick={openPicker} disabled={uploading || images.length >= maxImages} className="mt-3" data-testid="pick-photos-btn">
        <Upload className="h-4 w-4 mr-1"/>{uploading ? "Uploading…" : "Choose photos"}
      </Button>
    </div>
  );
}
